import { type CTNBreadcrumbLevel } from '@ctn/ui/types/ctn-breadcrumb';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import HelpModal from '@/components/HelpModal';
import useApiData from '@/hooks/useApiData';
import { getMessages } from '@/services/admin.service';

import { ROUTES } from '@/constants/routes.constants';
import type { IPageContext, UrlBack } from '@/interfaces/context/IPage.context.interface';

type PageProviderProps = {
	readonly children: ReactNode;
};

const contextDefaultValue: IPageContext = {
	breadcrumb: [],
	navigateData: undefined,
	setNavigateData: (param: any) => undefined,
	urlBack: undefined,
	setUrlBack: (param: any) => undefined,
	setBreadcrumb: (param: any) => undefined,
	isOpenHelpModal: false,
	setIsOpenHelpModal: (param: any) => undefined,
	updateMessages: () => undefined,
	onBackClick: () => undefined,
	appendBreadcrumb: (param: any, defaultBreadcrumb: CTNBreadcrumbLevel[]) => undefined,
	initBreadcrumb: (param: any) => undefined,
	replaceLastBreadcrumb: (param: any, url: string) => undefined,
	onClickBreadcrumb: (index: number) => undefined,
	scrollTop: () => undefined,
	numMessages: 0,
};

const PageContext = createContext<IPageContext>(contextDefaultValue);

const PageProvider: React.FC<PageProviderProps> = ({ children }: PageProviderProps): ReactElement => {
	const [breadcrumb, setBreadcrumb] = useState<CTNBreadcrumbLevel[] | undefined>(undefined);
	const [urlBack, setUrlBack] = useState<UrlBack>();
	const [navigateData, setNavigateData] = useState<Record<string, any> | undefined>(undefined);
	const [isOpenHelpModal, setIsOpenHelpModal] = useState(false);
	const [numMessages, setNumMessages] = useState(0);
	const loadingMessages = useRef(false);
	const { triggerFetch } = useApiData({ loading: ['messages'] as const });
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		if (!navigateData) return;

		const temporalNavigateData = { ...navigateData };
		const currentPath = location.pathname;

		for (const key in temporalNavigateData) {
			if (Object.hasOwn(temporalNavigateData, key)) {
				const itemUrls = temporalNavigateData[key]?.urls ?? [];
				const isMatch = itemUrls.some((url: string) => currentPath.includes(url));
				if (!isMatch) {
					temporalNavigateData[key] = { ...temporalNavigateData[key], urls: [] };
				}
			}
		}

		setNavigateData(temporalNavigateData);
	}, [location.pathname]);

	/** 
	 * UpdateMessages: Centraliza la actualización de mensajes para diversos componentes 
	 * (Home, trámites y polling periódico). 
	 * Se utiliza 'loadingMessages' (useRef) como flag para evitar llamadas 
	 * concurrentes innecesarias mientras hay una petición en curso.
	 */
	const updateMessages = useCallback(async () => {
		if (loadingMessages.current) return;

		loadingMessages.current = true;

		const { data } = await triggerFetch({
			fetch: getMessages,
			name: 'messages',
			error: 'Error al obtener mensajes',
		});

		loadingMessages.current = false;
		setNumMessages(data ?? 0);
	}, []);

	const appendBreadcrumb = useCallback(
		(newBreadcrumbLabel: string, defaultBreadcrumb: CTNBreadcrumbLevel[]) => {    
			setBreadcrumb((prevBreadcrumb) => {
				if (!prevBreadcrumb) return defaultBreadcrumb;
				const existingIndex = prevBreadcrumb.findIndex(
					(item) => item.url === location.pathname
				);

				if (existingIndex !== -1) {
					const slicedBreadcrumb = prevBreadcrumb.slice(0, existingIndex + 1);
					slicedBreadcrumb[existingIndex] = {
						...slicedBreadcrumb[existingIndex],
						label: newBreadcrumbLabel
					};

					return slicedBreadcrumb;
				}

				const lastBreadcrumb = prevBreadcrumb[prevBreadcrumb.length - 1];
				if (lastBreadcrumb && lastBreadcrumb.label === newBreadcrumbLabel) {
					return prevBreadcrumb; 
				}
				return [...prevBreadcrumb, { label: newBreadcrumbLabel, url: location.pathname }];
			});
			setUrlBack(undefined);
		},
		[location.pathname]
	);

	const initBreadcrumb = useCallback(
		(initialBreadcrumb: string) => {
			setBreadcrumb([{label: initialBreadcrumb, url: location.pathname}]);
		},
		[setBreadcrumb],
	);

	const onClickBreadcrumb = useCallback(
		(index: number) => {
			const targetBreadcrumb = breadcrumb?.[index];
			if (targetBreadcrumb?.url) {
				setBreadcrumb((prevBreadcrumb) => prevBreadcrumb?.slice(0, index + 1));
				navigate(targetBreadcrumb.url);
			}

			if (targetBreadcrumb?.callback) {
				targetBreadcrumb.callback();
			}
		},
		[breadcrumb, navigate],
	);

	const replaceLastBreadcrumb = useCallback(
		(newBreadcrumb: string, targetUrl?: string) => { 
			setBreadcrumb((prevBreadcrumb) => {
				const urlToAssign = targetUrl ?? location.pathname; 

				if (prevBreadcrumb?.length === 0) return [{ label: newBreadcrumb, url: urlToAssign }];

				const updatedBreadcrumb = [...prevBreadcrumb ?? []];
				updatedBreadcrumb[updatedBreadcrumb.length - 1] = { label: newBreadcrumb, url: urlToAssign };
				return updatedBreadcrumb;
			});
		},
		[location.pathname],
	);

	const scrollTop = () => {
		window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

		let lastHeight = document.documentElement.scrollHeight;

		const resizeObserver = new ResizeObserver(() => {
			const currentHeight = document.documentElement.scrollHeight;
        
			if (currentHeight > lastHeight) {
				window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
				resizeObserver.disconnect();
			}
			lastHeight = currentHeight;
		});

		resizeObserver.observe(document.documentElement);
		setTimeout(() => resizeObserver.disconnect(), 500);
	};

	const onBackClick = useCallback(() => {
		if (urlBack?.navigate) {
			urlBack.navigate();
			setUrlBack({}); 
			return;
		}

		setUrlBack({});

		if (!breadcrumb || breadcrumb.length <= 1) {
			navigate(ROUTES.HOME);
			return;
		}

		const previousIndex = breadcrumb.length - 2;
		const targetBreadcrumb = breadcrumb[previousIndex];

		if (targetBreadcrumb?.url) {
			setBreadcrumb((prev) => prev?.slice(0, previousIndex + 1));
			navigate(targetBreadcrumb.url);
		} else {
			navigate(ROUTES.HOME);
		}
	}, [urlBack, breadcrumb, navigate]);

	const value: IPageContext = useMemo(
		() => ({
			isOpenHelpModal,
			urlBack,
			setUrlBack,
			setIsOpenHelpModal,
			updateMessages,
			navigateData,
			setNavigateData,
			numMessages,
			breadcrumb,
			setBreadcrumb,
			appendBreadcrumb,
			initBreadcrumb,
			replaceLastBreadcrumb,
			onBackClick,
			onClickBreadcrumb,
			scrollTop,
		}),
		[breadcrumb, isOpenHelpModal, updateMessages, numMessages, urlBack, setUrlBack, navigate],
	);

	return (
		<PageContext.Provider value={value}>
			{children}
			<HelpModal isShow={isOpenHelpModal} onClose={() => setIsOpenHelpModal(false)} />
		</PageContext.Provider>
	);
};

const usePageContext = (): IPageContext => {
	const context = useContext(PageContext);

	if (context === null) {
		throw new Error('usePage must be used within provider');
	}

	return context;
};

export { PageContext, PageProvider, usePageContext };
